import * as React from 'react';
import { CartesianGrid, Customized, Label, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import ChartTooltip from './ChartTooltip';
import { Config } from '../helpers';

const markerFillTypes = {
  filled: 'filled',
  empty: 'empty',
};

const lineStyles = {
  solid: null,
  dashed: "6 4",
  dotted: "2 2",
};

const markerRenderers = {
  circle: ({ cx, cy, stroke, index, isFilled }) => (
    <circle
      cx={cx}
      cy={cy}
      r={4}
      fill={isFilled ? stroke : 'white'}
      stroke={stroke}
      strokeWidth={2}
      key={`circle-${index}`}
    />
  ),

  square: ({ cx, cy, stroke, index, isFilled }) => (
    <rect
      x={cx - 4}
      y={cy - 4}
      width={8}
      height={8}
      fill={isFilled ? stroke : 'white'}
      stroke={stroke}
      strokeWidth={2}
      key={`square-${index}`}
    />
  ),

  triangle: ({ cx, cy, stroke, index, isFilled }) => (
    <polygon
      key={`triangle-${index}`}
      points={`${cx},${cy - 5} ${cx + 5},${cy + 5} ${cx - 5},${cy + 5}`}
      fill={isFilled ? stroke : 'white'}
      stroke={stroke}
      strokeWidth={2}
    />
  ),

  diamond: ({ cx, cy, stroke, index, isFilled }) => (
    <polygon
      key={`diamond-${index}`}
      points={`${cx},${cy - 5} ${cx + 5},${cy} ${cx},${cy + 5} ${cx - 5},${cy}`}
      fill={isFilled ? stroke : 'white'}
      stroke={stroke}
      strokeWidth={2}
    />
  ),

  cross: ({ cx, cy, stroke, index }) => (
    <g key={`cross-${index}`} stroke={stroke} strokeWidth={2}>
      <line x1={cx - 5} y1={cy} x2={cx + 5} y2={cy}/>
      <line x1={cx} y1={cy - 5} x2={cx} y2={cy + 5}/>
    </g>
  ),
};

function buildMaps(chartConfig) {
  const colorsMap = {};
  const lineStylesMap = {};
  const markersMap = {};
  const markerFillMap = {};
  chartConfig.colorsMap.forEach(entry => {
    colorsMap[entry.id] = entry.color;
    lineStylesMap[entry.id] = entry.lineStyle;
    markersMap[entry.id] = entry.markerShape;
    markerFillMap[entry.id] = entry.markerFill || markerFillTypes.filled;
  });
  return { colorsMap, lineStylesMap, markersMap, markerFillMap };
}

// A colorsMap id may be a prefix of a group id, so one entry can style a whole
// family (`deepseek-r1|b200` covers every precision under it). The LONGEST match
// wins, which makes an exact id beat a family and keeps the result independent
// of entry order — it matters because a group field missing from a row leaves an
// empty trailing segment, and that id (`…|mtp|`) is itself a prefix of the
// complete one (`…|mtp|agg`).
function resolveByPrefix(map, gid, fallback) {
  let result = fallback;
  let matched = -1;
  Object.entries(map).forEach(([k, v]) => {
    const key = k.toLowerCase();
    if (gid.startsWith(key) && key.length > matched) {
      matched = key.length;
      result = v;
    }
  });
  return result;
}

function renderLegend(colorsMap, lineStylesMap, markersMap, markerFillMap) {
  return ({ payload }) => (
    <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', padding: '10px 0' }}>
      {payload.filter(entry => !String(entry.dataKey).endsWith('__dom')).map((entry, index) => {
        const gid = entry.dataKey.toLowerCase();
        // Band hi edge (`__band`) is always dashed, regardless of the group's own style.
        const isBandEdge = gid.endsWith('__band');
        const lineStyleDash = isBandEdge ? lineStyles.dashed : (lineStyles[resolveByPrefix(lineStylesMap, gid, null)] ?? null);
        const markerShapeName = resolveByPrefix(markersMap, gid, 'circle');
        const isFilled = resolveByPrefix(markerFillMap, gid, markerFillTypes.filled) === markerFillTypes.filled;

        return (
          <div
            key={`legend-item-${index}`}
            style={{ display: 'flex', alignItems: 'center', marginRight: '20px', marginBottom: '5px' }}
          >
            <svg width="40" height="20" style={{ marginRight: '5px' }}>
              <line x1="0" y1="10" x2="30" y2="10" stroke={entry.color} strokeWidth="2" strokeDasharray={lineStyleDash}/>
              {markerShapeName === 'circle' && (
                <circle cx="15" cy="10" r="4" fill={isFilled ? entry.color : 'white'} stroke={entry.color} strokeWidth="2"/>
              )}
              {markerShapeName === 'square' && (
                <rect x="11" y="6" width="8" height="8" fill={isFilled ? entry.color : 'white'} stroke={entry.color} strokeWidth="2"/>
              )}
              {markerShapeName === 'triangle' && (
                <polygon points="15,5 20,15 10,15" fill={isFilled ? entry.color : 'white'} stroke={entry.color} strokeWidth="2"/>
              )}
              {markerShapeName === 'diamond' && (
                <polygon points="15,5 20,10 15,15 10,10" fill={isFilled ? entry.color : 'white'} stroke={entry.color} strokeWidth="2"/>
              )}
              {markerShapeName === 'cross' && (
                <g stroke={entry.color} strokeWidth="2">
                  <line x1="10" y1="10" x2="20" y2="10"/>
                  <line x1="15" y1="5" x2="15" y2="15"/>
                </g>
              )}
            </svg>
            <span style={{ fontSize: '14px' }}>{entry.value}</span>
          </div>
        );
      })}
    </div>
  );
}

// Shaded band fill (§1.5, band.mode:"area"), drawn in a <Customized> layer so it
// can use the chart's real axis scales (passed via Recharts props/state) to map
// each group's lo/hi percentile curves to pixels and fill between them. Rendered
// before the lines so the edge lines and dots stay on top.
function BandLayer({ xAxisMap, yAxisMap, groups }) {
  const sx = xAxisMap && Object.values(xAxisMap)[0]?.scale;
  const sy = yAxisMap && Object.values(yAxisMap)[0]?.scale;
  if (!sx || !sy || !groups?.length) return null;
  return (
    <g className='nv-chart__band'>
      {groups.map(g => {
        if (!g.lo.length || !g.hi.length) return null;
        const forward = g.lo.map(p => `${sx(p.x)},${sy(p.y)}`);
        const backward = g.hi.slice().reverse().map(p => `${sx(p.x)},${sy(p.y)}`);
        return (
          <polygon key={g.id} points={[...forward, ...backward].join(' ')} fill={g.color} fillOpacity={0.15} stroke='none'/>
        );
      })}
    </g>
  );
}

export default function LineChartWidget({ chartData, chartGroups, chartConfig, lineType = Config.Styles.Chart.DefaultLineStyle, bandArea = null }) {
  const [hoveredDot, setHoveredDot] = React.useState(null);
  const { colorsMap, lineStylesMap, markersMap, markerFillMap } = buildMaps(chartConfig);

  // Resolve each band group's colour once (same prefix logic as the lines) so
  // the fill matches its edge lines; the pixel mapping happens inside BandLayer.
  const bandGroups = React.useMemo(
    () => (bandArea || []).map(g => ({ ...g, color: resolveByPrefix(colorsMap, g.id.toLowerCase(), '#000') })),
    [bandArea, colorsMap]
  );

  return (
    <div>
      <ResponsiveContainer width={Config.Styles.Chart.Width} height={Config.Styles.Chart.Height}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3"/>
          {bandGroups.length > 0 && <Customized component={<BandLayer groups={bandGroups}/>}/>}
          <XAxis
            type="number"
            tickFormatter={value => value}
            dataKey={chartConfig.axes.x.field}
            tick={Config.Styles.Chart.Axis.Tick}
            axisLine={Config.Styles.Chart.Axis.AxisLine}>
            <Label value={chartConfig.axes.x.label} position="bottom" offset={0}/>
          </XAxis>
          <YAxis
            type="number"
            tickFormatter={value => value}
            tick={Config.Styles.Chart.Axis.Tick}
            axisLine={Config.Styles.Chart.Axis.AxisLine}>
            <Label value={chartConfig.axes.y.label} angle={-90} position="inside" dx={-35}/>
          </YAxis>
          <Tooltip cursor={false} isAnimationActive={false} content={<ChartTooltip chartConfig={chartConfig} hoveredDot={hoveredDot}/>}/>
          <Legend content={renderLegend(colorsMap, lineStylesMap, markersMap, markerFillMap)}/>

          {chartGroups.map((g) => {
            const gid = g.id.toLowerCase();
            const color = resolveByPrefix(colorsMap, gid, '#000');
            // A band's hi edge is dashed to read as the upper bound of the range (§1.5).
            const lineStyle = g.band === 'hi'
              ? lineStyles.dashed
              : (lineStyles[resolveByPrefix(lineStylesMap, gid, null)] ?? null);
            const markerShape = markerRenderers[resolveByPrefix(markersMap, gid, 'circle')] ?? markerRenderers.circle;
            const isFilled = resolveByPrefix(markerFillMap, gid, markerFillTypes.filled) === markerFillTypes.filled;

            return (
              <Line
                key={g.id}
                dataKey={g.id}
                name={g.label}
                connectNulls={true}
                type={lineType}
                strokeWidth={2}
                stroke={color}
                strokeDasharray={lineStyle}
                dot={({ value, payload: dotPayload, ...p }) => {
                  if (value === null) return null;
                  return (
                    <g
                      key={`dot-${g.id}-${p.index}`}
                      onMouseEnter={() => setHoveredDot({ dataKey: g.id, entry: dotPayload[`${g.id}__entry`], stroke: color, xField: g.xField })}
                      onMouseLeave={() => setHoveredDot(null)}
                    >
                      <circle cx={p.cx} cy={p.cy} r={10} fill="transparent"/>
                      {markerShape({...p, isFilled})}
                    </g>
                  );
                }}
                activeDot={null}
              />
            );
          })}

          {chartConfig.pareto?.frontierOnly && chartConfig.pareto?.showDominated && chartGroups.map((g) => {
            const gid = g.id.toLowerCase();
            const color = resolveByPrefix(colorsMap, gid, '#000');
            const markerShape = markerRenderers[resolveByPrefix(markersMap, gid, 'circle')] ?? markerRenderers.circle;
            // Dominated (off-frontier) points: markers only — no connecting line,
            // no legend entry. Hollow fill sets them apart from frontier points.
            return (
              <Line
                key={`${g.id}__dom`}
                dataKey={`${g.id}__dom`}
                connectNulls={false}
                stroke="none"
                legendType="none"
                isAnimationActive={false}
                dot={({ value, payload: dotPayload, ...p }) => {
                  if (value === null || value === undefined) return null;
                  return (
                    <g
                      key={`dom-${g.id}-${p.index}`}
                      onMouseEnter={() => setHoveredDot({ dataKey: g.id, entry: dotPayload[`${g.id}__dom__entry`], stroke: color, xField: g.xField })}
                      onMouseLeave={() => setHoveredDot(null)}
                    >
                      <circle cx={p.cx} cy={p.cy} r={10} fill="transparent"/>
                      {markerShape({...p, isFilled: false})}
                    </g>
                  );
                }}
                activeDot={null}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
